import React, { useEffect, useState } from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { Translate, Storage } from 'react-jhipster';
import { useAppSelector } from 'app/config/store';
import ErrorBoundary from 'app/shared/error/error-boundary';
import { TabSessionManager } from 'app/shared/util/tab-session-manager';
import TransferOtp from 'app/modules/transfer-otp/transfer-otp';

// Quotation bought pages are opened by customers from the transfer SMS: show the OTP screen there instead of the login page (ALSHMOKH-34)
const BOUGHT_PAGE_PATH = /^\/quotation\/(boubyan\/)?bought\/(\d+)\/?$/;

interface IOwnProps extends RouteProps {
  hasAnyAuthorities?: string[];
}

export const PrivateRouteComponent = ({ component: Component, hasAnyAuthorities = [], ...rest }: IOwnProps) => {
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const sessionHasBeenFetched = useAppSelector(state => state.authentication.sessionHasBeenFetched);
  const account = useAppSelector(state => state.authentication.account);
  const isAuthorized = hasAnyAuthority(account.authorities, hasAnyAuthorities);

  const isLoggedIn = Storage.session.get('account');
  const isLoggedOut = Storage.session.get('logout');
  const userAuthorities = JSON.parse(localStorage.getItem('authorities')) || [];

  // Check if we have a token in storage
  const hasToken = TabSessionManager.isSessionValid();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    TabSessionManager.syncToken();
    // Set loading to false once session has been fetched
    const timer = setTimeout(() => {
      if (sessionHasBeenFetched || !hasToken) {
        setLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [sessionHasBeenFetched, hasToken]);

  const checkAuthorities = props =>
    isAuthorized ? (
      <ErrorBoundary>
        <Component {...props} />
      </ErrorBoundary>
    ) : (
      <div className="insufficient-authority">
        <div className="alert alert-danger">
          <Translate contentKey="error.http.403">You are not authorized to access this page.</Translate>
        </div>
      </div>
    );

  const renderRedirect = props => {
    // If we're still loading and have a token, wait for session to be fetched
    if (loading && hasToken) {
      return <h5 style={{ textAlign: 'center' }}>Loading...</h5>;
    }

    const boughtPage = props.location.pathname.match(BOUGHT_PAGE_PATH);
    if (boughtPage && !loading && !isAuthenticated) {
      const staffLogin = {
        pathname: boughtPage[1] ? '/boubyan/login' : '/login',
        state: { from: props.location }
      };
      return <TransferOtp quotationId={boughtPage[2]} staffLogin={staffLogin} />;
    }

    // If no token exists, redirect to login immediately with current location
    if (!hasToken && !loading) {
      return (
        <Redirect
          to={{
            pathname: props.location.pathname.startsWith('/quotation/boubyan/') ? '/boubyan/login' : '/login',
            state: { from: props.location },
          }}
        />
      );
    }

    // If session has been fetched or we're not loading anymore
    if ((sessionHasBeenFetched || !loading) && hasToken) {
      if (isAuthenticated) {
        return checkAuthorities(props);
      } else if (!loading) {
        setTimeout(() => {
          if (!isAuthenticated) {
            return (
              <Redirect
                to={{
                  pathname: props.location.pathname.startsWith('/quotation/boubyan/') ? '/boubyan/login' : '/login',
                  state: { from: props.location }
                }}
              />
            );
          }
        }, 500);

        return <h5 style={{ textAlign: 'center' }}>Verifying session...</h5>;
      }
    }

    // Default loading state
    return <div>Loading...</div>;
  };

  if (!Component) throw new Error(`A component needs to be specified for private route for path ${(rest as any).path}`);

  return <Route {...rest} render={renderRedirect} />;
};

export const hasAnyAuthority = (authorities: string[], hasAnyAuthorities: string[]) => {
  if (authorities && authorities.length !== 0) {
    if (hasAnyAuthorities.length === 0) {
      return true;
    }
    return hasAnyAuthorities.some(auth => authorities.includes(auth));
  }
  return false;
};

/**
 * A route wrapped in an authentication check so that routing happens only when you are authenticated.
 * Accepts same props as React router Route.
 * The route also checks for authorization if hasAnyAuthorities is specified.
 */
export default PrivateRouteComponent;
